import type { AABB } from '../colliders'
import type { DaemonPluginHealth, NormalizedRun } from '../../api/types'

export const WORKSHOP_BUILD_STATIONS = [
  { id: 'west-south', x: -7.5, z: 3.0, rotY: 90 },
  { id: 'west-north', x: -7.5, z: -0.5, rotY: 90 },
  { id: 'east-south', x: 7.5, z: 3.0, rotY: 270 },
  { id: 'east-north', x: 7.5, z: -0.5, rotY: 270 },
] as const

export const WORKSHOP_POD_RUGS = [
  { id: 'west', x: -7.5, z: 1.25 },
  { id: 'east', x: 7.5, z: 1.25 },
] as const

export const WORKSHOP_TOOL_SHELVES = [
  { id: 'west-outer', x: -11.5, z: -8.9 },
  { id: 'west-inner', x: -8.7, z: -8.9 },
  { id: 'east-inner', x: 8.7, z: -8.9 },
  { id: 'east-outer', x: 11.5, z: -8.9 },
] as const

export const WORKSHOP_TEST_CELL = {
  centerX: 0,
  centerZ: -6.1,
  width: 7,
  depth: 5,
} as const

export const WORKSHOP_TEST_TABLE = {
  x: 0,
  z: -6.15,
} as const

/** Clear route through the 1.5 m south-centre glass doorway. */
export const WORKSHOP_TEST_CELL_DOOR_APPROACH: AABB = {
  minX: -0.75,
  maxX: 0.75,
  minY: 0,
  maxY: 2.8,
  minZ: WORKSHOP_TEST_CELL.centerZ + WORKSHOP_TEST_CELL.depth / 2 - 0.15,
  maxZ: WORKSHOP_TEST_CELL.centerZ + WORKSHOP_TEST_CELL.depth / 2 + 1.65,
}

/** Minimum 2.4 m entry-to-test circulation route between the two build pods. */
export const WORKSHOP_CENTRAL_SPINE: AABB = {
  minX: -1.2,
  maxX: 1.2,
  minY: 0,
  maxY: 2.8,
  minZ: WORKSHOP_TEST_CELL_DOOR_APPROACH.maxZ,
  maxZ: 10,
}

/** Small physical test surface inside the cell; traversal routes around it. */
export const WORKSHOP_TEST_TABLE_COLLIDER: AABB = {
  minX: WORKSHOP_TEST_TABLE.x - 0.7,
  maxX: WORKSHOP_TEST_TABLE.x + 0.7,
  minY: 0,
  maxY: 0.8,
  minZ: WORKSHOP_TEST_TABLE.z - 0.7,
  maxZ: WORKSHOP_TEST_TABLE.z + 0.7,
}

export const MAX_WORKSHOP_BUILDS = WORKSHOP_BUILD_STATIONS.length
export const MAX_WORKSHOP_PLUGINS = 8

export interface WorkshopBuildSlot {
  id: string
  stationId: typeof WORKSHOP_BUILD_STATIONS[number]['id']
}

export interface WorkshopPluginSlot {
  id: string
  name: string
  kind: string
  status: DaemonPluginHealth['status']
}

export interface WorkshopProjectionState {
  state: 'fresh-empty' | 'fresh-populated' | 'loading' | 'stale' | 'disconnected'
  builds: readonly WorkshopBuildSlot[]
  plugins: readonly WorkshopPluginSlot[]
  totalBuilds: number
  totalPlugins: number
  healthyPlugins: number
  attentionPlugins: number
  buildOverflow: number
  pluginOverflow: number
}

const compareText = (first: string, second: string): number =>
  first < second ? -1 : first > second ? 1 : 0

/** Deterministic, bounded layout of Portal-projected build and plugin truth. */
export function layoutWorkshopProjection(
  status: 'disconnected' | 'loading' | 'fresh' | 'stale',
  runs: readonly NormalizedRun[],
  plugins: readonly DaemonPluginHealth[],
  lastGood?: WorkshopProjectionState,
): WorkshopProjectionState {
  if (status === 'stale') {
    return {
      state: 'stale',
      builds: lastGood?.builds ?? [],
      plugins: lastGood?.plugins ?? [],
      totalBuilds: lastGood?.totalBuilds ?? 0,
      totalPlugins: lastGood?.totalPlugins ?? 0,
      healthyPlugins: lastGood?.healthyPlugins ?? 0,
      attentionPlugins: lastGood?.attentionPlugins ?? 0,
      buildOverflow: lastGood?.buildOverflow ?? 0,
      pluginOverflow: lastGood?.pluginOverflow ?? 0,
    }
  }
  if (status !== 'fresh') {
    return {
      state: status,
      builds: [],
      plugins: [],
      totalBuilds: 0,
      totalPlugins: 0,
      healthyPlugins: 0,
      attentionPlugins: 0,
      buildOverflow: 0,
      pluginOverflow: 0,
    }
  }
  const activeRuns = runs
    .filter((run) => run.status === 'running' && !run.stale)
    .sort((first, second) => compareText(first.id, second.id))
  const orderedPlugins = [...plugins].sort((first, second) =>
    compareText(first.name, second.name) ||
    compareText(first.kind, second.kind) ||
    compareText(first.status, second.status))
  const builds = activeRuns
    .slice(0, MAX_WORKSHOP_BUILDS)
    .map((run, index): WorkshopBuildSlot => ({
      id: run.id,
      stationId: WORKSHOP_BUILD_STATIONS[index].id,
    }))
  const projectedPlugins = orderedPlugins
    .slice(0, MAX_WORKSHOP_PLUGINS)
    .map((plugin): WorkshopPluginSlot => ({
      id: `${plugin.kind}:${plugin.name}`,
      name: plugin.name,
      kind: plugin.kind,
      status: plugin.status,
    }))
  return {
    state: builds.length === 0 && projectedPlugins.length === 0
      ? 'fresh-empty'
      : 'fresh-populated',
    builds,
    plugins: projectedPlugins,
    totalBuilds: activeRuns.length,
    totalPlugins: orderedPlugins.length,
    healthyPlugins: orderedPlugins.filter((plugin) => plugin.status === 'healthy').length,
    attentionPlugins: orderedPlugins.filter(
      (plugin) => plugin.status === 'degraded' || plugin.status === 'down',
    ).length,
    buildOverflow: Math.max(0, activeRuns.length - MAX_WORKSHOP_BUILDS),
    pluginOverflow: Math.max(0, orderedPlugins.length - MAX_WORKSHOP_PLUGINS),
  }
}

export function workshopProjectionKey(state: WorkshopProjectionState): string {
  return JSON.stringify({
    state: state.state,
    builds: state.builds.map((build) => [build.id, build.stationId]),
    plugins: state.plugins.map((plugin) => [
      plugin.id,
      plugin.name,
      plugin.kind,
      plugin.status,
    ]),
    totalBuilds: state.totalBuilds,
    totalPlugins: state.totalPlugins,
    healthyPlugins: state.healthyPlugins,
    attentionPlugins: state.attentionPlugins,
    buildOverflow: state.buildOverflow,
    pluginOverflow: state.pluginOverflow,
  })
}

/** Physical panel copy derived only from the bounded projection snapshot. */
export function workshopProjectionLines(
  state: WorkshopProjectionState,
): readonly [string, string, string] {
  const projected =
    state.state === 'fresh-empty' ||
    state.state === 'fresh-populated' ||
    state.state === 'stale'
  if (!projected) {
    return [
      'TOOL CATALOGUE · NOT PROJECTED',
      'PLUGIN HEALTH · UNAVAILABLE',
      'BUILD QUEUE · UNAVAILABLE',
    ]
  }
  return [
    `${state.totalPlugins} AUTHORIZED PLUGINS`,
    `${state.healthyPlugins} HEALTHY · ${state.attentionPlugins} NEED ATTENTION`,
    state.totalBuilds > 0
      ? `${state.totalBuilds} BUILDS IN FLIGHT`
      : 'BUILD BENCHES READY',
  ]
}
